import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, SITE } from "@/data/site";

const navId = (to) => (to === "/" ? "home" : to.replace(/\//g, ""));

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-[#1e1e26] bg-[#0A0A0C]/92 shadow-lg shadow-black/30 backdrop-blur-xl"
          : "border-white/[0.06] bg-[#0A0A0C]/60 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 w-full max-w-[1340px] items-center gap-4 px-5 sm:px-8 lg:h-[68px] lg:px-6">
        <Link to="/" aria-label="Kensmarc Electric home" data-testid="header-logo-link" className="shrink-0">
          <Logo />
        </Link>

        {/* Desktop nav — every label stays on one line */}
        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 lg:flex xl:gap-1" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${navId(link.to)}`}
              className={`relative whitespace-nowrap rounded-md px-2 py-2 text-sm font-medium transition-colors xl:px-3 ${
                isActive(link.to) ? "text-amber-400" : "text-neutral-300 hover:bg-white/[0.04] hover:text-white"
              }`}
            >
              {link.label}
              {isActive(link.to) && <span className="absolute inset-x-2.5 -bottom-0.5 h-0.5 rounded-full bg-amber-400" />}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
          <a
            href={SITE.phonePrimary.tel}
            data-testid="header-call-link"
            className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full border border-[#26262f] px-3 text-sm font-semibold text-white transition-colors hover:border-amber-400/50 hover:text-amber-400 md:border-transparent md:px-2 2xl:px-3"
            aria-label={`Call ${SITE.phonePrimary.display}`}
          >
            <Phone className="h-4 w-4 text-amber-400" />
            <span className="hidden xl:inline">{SITE.phonePrimary.display}</span>
          </a>
          <Link
            to="/request-a-quote"
            data-testid="header-quote-btn"
            className="hidden h-10 items-center whitespace-nowrap rounded-full bg-amber-400 px-4 text-sm font-bold uppercase tracking-wide text-[#0A0A0C] transition-all hover:bg-amber-300 hover:shadow-lg hover:shadow-amber-400/25 sm:inline-flex 2xl:px-5"
          >
            Request a Quote
          </Link>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#26262f] text-white transition-colors hover:border-amber-400/50 lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col border-l border-[#20202a] bg-[#0d0d12] shadow-2xl lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              data-testid="mobile-menu"
            >
              <div className="flex h-16 items-center justify-between border-b border-[#1c1c24] px-5">
                <Logo size="sm" />
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#26262f] text-white"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" aria-label="Mobile">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`mobile-nav-${navId(link.to)}`}
                    className={`rounded-lg px-4 py-3 text-base font-medium ${
                      isActive(link.to) ? "bg-amber-400/10 text-amber-400" : "text-neutral-200 hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
              <div className="space-y-3 border-t border-[#1c1c24] p-4">
                <a
                  href={SITE.phonePrimary.tel}
                  data-testid="mobile-call-link"
                  className="flex items-center justify-center gap-2 rounded-lg border border-[#2a2a34] py-3 font-semibold text-white"
                >
                  <Phone className="h-4 w-4 text-amber-400" />
                  {SITE.phonePrimary.display}
                </a>
                <button
                  type="button"
                  onClick={() => navigate("/request-a-quote")}
                  data-testid="mobile-quote-btn"
                  className="w-full rounded-lg bg-amber-400 py-3 text-base font-bold uppercase tracking-wide text-[#0A0A0C]"
                >
                  Request a Quote
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
